import React, { createContext, FC, memo, useContext, useEffect, useState } from 'react';
import { AuthContext } from 'routes/auth.context';
import { Background, ContainerSticky } from './AlertConfirm/styl';
import { Warp, GroupRoot } from './base/styl';
import styled from 'styled-components';

const Container = styled.div`
    @keyframes shake {
        0%, 100% { transform: translateX(0); }
        10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
        20%, 40%, 60%, 80% { transform: translateX(5px); }
    }

    animation: shake 0.8s ease-out;
`;

const AlertCard = styled.div`
    background: linear-gradient(135deg, #45246E 0%, #45246E 100%);
    border-radius: 20px;
    padding: 40px;
    text-align: center;
    color: white;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
    position: relative;
    overflow: hidden;
    border: 3px solid rgba(255, 255, 255, 0.2);

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 4px;
        background: linear-gradient(90deg, #FFBD24, #FFBD24, #FFBD24);
        animation: flash 2s infinite;
    }

    @keyframes flash {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
    }
`;

const Icon = styled.div`
    font-size: 4rem;
    margin-bottom: 20px;
    animation: pulse 0.8s infinite;
`;

const Title = styled.h1`
    font-size: 2.2rem;
    margin: 0 0 15px 0;
    font-weight: 700;
    color: white;
`;

const Subtitle = styled.p`
    font-size: 1.1rem;
    margin: 0 0 25px 0;
    opacity: 0.95;
    line-height: 1.5;
    color: white;
`;

const BalanceDisplay = styled.div`
    background: rgba(255, 255, 255, 0.1);
    border-radius: 15px;
    padding: 20px;
    margin: 20px 0;
    border: 2px solid rgba(255, 255, 255, 0.2);
`;

const BalanceAmount = styled.div`
    font-size: 2rem;
    font-weight: bold;
    margin-bottom: 5px;
    color: white;
`;

const BalanceLabel = styled.div`
    font-size: 0.9rem;
    opacity: 0.8;
    color: white;
`;

const Button = styled.button`
    background: rgba(255, 255, 255, 0.9);
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50px;
    color: #000;
    padding: 15px 35px;
    font-size: 1.1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    margin: 5px;
    backdrop-filter: blur(10px);

    &:hover {
        background: white;
        transform: translateY(-2px);
        box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
    }

    &:active {
        transform: translateY(0);
    }

    &.secondary {
        background: rgba(255, 255, 255, 0.2);
        color: white;

        &:hover {
            background: rgba(255, 255, 255, 0.3);
        }
    }
`;

const ButtonGroup = styled.div`
    display: flex;
    gap: 10px;
    justify-content: center;
    flex-wrap: wrap;
`;

const CloseButton = styled.button`
    position: absolute;
    top: 20px;
    right: 20px;
    background: rgba(255, 255, 255, 0.1);
    border: none;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    color: white;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    color:white;

    &:hover {
        background: rgba(255, 255, 255, 0.2);
        transform: scale(1.1);
    }
`;

export const BalanceAlertContext = createContext<{
    showBalanceAlert: (unidade: any) => void;
    closeBalanceAlert: () => void;
}>({
    showBalanceAlert: () => {},
    closeBalanceAlert: () => {},
});

const BALANCE_ALERT_SHOWN_KEY = '@balance-alert-shown';

export const BalanceAlertProvider: FC<{ children: React.ReactNode }> = memo(({ children }) => {
    const { signed, unidadeSelecionada } = useContext(AuthContext);
    const [showModal, setShowModal] = useState(false);
    const [alertUnidade, setAlertUnidade] = useState<any>(null);

    // Configurações do limite de saldo
    const LOW_BALANCE_LIMIT = 50; // Valor mínimo antes de alertar

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value);
    };

    useEffect(() => {
        // Apenas mostrar quando logado e tem unidade selecionada
        if (signed && unidadeSelecionada && unidadeSelecionada.saldo && unidadeSelecionada?.tipoPagamento === 'PRE_PAGO') {
            const currentBalance = unidadeSelecionada.saldo.total || 0;
            const todayKey = new Date().toDateString();

            // Verificar se o saldo está baixo e se o alerta já foi mostrado hoje
            if (currentBalance <= LOW_BALANCE_LIMIT && !localStorage.getItem(`${BALANCE_ALERT_SHOWN_KEY}-${todayKey}`)) {
                setAlertUnidade(unidadeSelecionada);
                setShowModal(true);
            }
        }
    }, [signed, unidadeSelecionada]);

    // Limpar alerta ao deslogar
    useEffect(() => {
        if (!signed) {
            setShowModal(false);
            setAlertUnidade(null);
        }
    }, [signed]);

    const closeBalanceAlert = () => {
        setShowModal(false);
        setAlertUnidade(null);
        // Marcar como mostrado hoje
        const todayKey = new Date().toDateString();
        localStorage.setItem(`${BALANCE_ALERT_SHOWN_KEY}-${todayKey}`, 'true');
    };

    const showBalanceAlert = (unidadeData: any) => {
        setAlertUnidade(unidadeData);
        setShowModal(true);
    };

    return (
        <BalanceAlertContext.Provider value={{ showBalanceAlert, closeBalanceAlert }}>
            {children}
            {showModal && alertUnidade && (
                <GroupRoot>
                    <Background />
                    <ContainerSticky>
                        <Container>
                            <AlertCard>
                                <CloseButton onClick={closeBalanceAlert}>
                                    <span style={{ color: 'white', fontSize: '2rem' }}>×</span>
                                </CloseButton>
                                <Icon>🚨</Icon>
                                <Title>Saldo Baixo!</Title>
                                <Subtitle>
                                    O saldo do estabelecimento <strong style={{ color: '#FFBD24', fontSize: '1.6rem' }}>{alertUnidade.nomeFantasia || alertUnidade.razaoSocial}</strong> está muito baixo.
                                    <br />
                                    Por favor, recarregue o saldo para continuar utilizando os serviços.
                                </Subtitle>

                                <BalanceDisplay>
                                    <BalanceAmount>
                                        {formatCurrency(alertUnidade.saldo?.total || 0)}
                                    </BalanceAmount>
                                    <BalanceLabel>Saldo Atual</BalanceLabel>
                                </BalanceDisplay>

                                <ButtonGroup>
                                    <Button onClick={closeBalanceAlert}>
                                        Entendi
                                    </Button>
                                    <Button className="secondary" onClick={closeBalanceAlert}>
                                        Lembrar Depois
                                    </Button>
                                </ButtonGroup>
                            </AlertCard>
                        </Container>
                    </ContainerSticky>
                </GroupRoot>
            )}
        </BalanceAlertContext.Provider>
    );
});

export const useBalanceAlert = () => useContext(BalanceAlertContext);
